import { isDevMode } from '@angular/core';
import {
  SdCountCellItem,
  TeammateIconOutlineList,
  TeammateIconOutlineName,
} from '@serious-system';

export type TeammateAvatarShape =
  'pentagon' | 'square' | 'circle' | 'hexagon' | 'losange';

export type TeammateAvatarEyes = '1' | '2' | '3' | '4' | '5';

/**
 * Avatar axes as persisted on the teammate (match the API `TeammateView` enums).
 * `TeammateColor` selects a base/dark palette; `TeammateEyesEnum` is the spelled-out
 * DB form of the eye variant (Prisma enum values can't start with a digit) and maps
 * to the UI `TeammateAvatarEyes` ('1'..'5').
 */
export type TeammateColor = 'A' | 'B' | 'C' | 'D' | 'E';
export type TeammateEyesEnum = 'one' | 'two' | 'three' | 'four' | 'five';

export interface TeammateAvatarConfig {
  baseColor: string;
  darkColor: string;
  shape: TeammateAvatarShape;
  eyes: TeammateAvatarEyes;
}

export type TeammateAvatarCropContext = 'x-small' | 'small' | 'large';

export const TEAMMATE_AVATAR_PRESETS: Record<
  'A' | 'B' | 'C' | 'D' | 'E',
  TeammateAvatarConfig
> = {
  A: {
    baseColor: '#B8CDFF',
    darkColor: '#7579FF',
    shape: 'pentagon',
    eyes: '1',
  },
  B: {
    baseColor: '#ABACFF',
    darkColor: '#A27CFF',
    shape: 'square',
    eyes: '2',
  },
  C: {
    baseColor: '#C7B1FF',
    darkColor: '#B66DFF',
    shape: 'circle',
    eyes: '1',
  },
  D: {
    baseColor: '#DFAFFF',
    darkColor: '#D272FF',
    shape: 'hexagon',
    eyes: '4',
  },
  E: {
    baseColor: '#F4BEF3',
    darkColor: '#EF75EA',
    shape: 'losange',
    eyes: '3',
  },
};

/** DB eyes enum (`one`..`five`) → UI eyes id (`'1'`..`'5'`). */
const EYES_BY_ENUM: Record<TeammateEyesEnum, TeammateAvatarEyes> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
};

/**
 * The shape and face a teammate starts from when it has none of its own — a
 * teammate wearing an icon visual (SB-3519) carries `shape: null` and `eyes: null`.
 * Deliberately the same pair the Builder seeds a brand-new teammate with, so the
 * default-visual tab of an icon teammate and a fresh teammate look alike.
 */
const AVATAR_FALLBACK: {
  shape: TeammateAvatarShape;
  eyes: TeammateEyesEnum;
} = { shape: 'pentagon', eyes: 'one' };

/**
 * Resolve a teammate's avatar from its axes (SB-3200 Decision F). The base/dark
 * palette comes from the per-color preset; shape passes through; the spelled-out
 * DB eyes enum maps to the UI '1'..'5' form.
 *
 * SB-3519: `shape` and `eyes` are null for a teammate wearing an icon, so they
 * fall back rather than throwing. **This function does not decide which visual a
 * teammate wears** — it only builds the avatar one. Dispatching between the two
 * is the visual seam's job; until that lands, an icon teammate still renders the
 * avatar it would get from the fallback.
 */
export function getTeammateAvatarFromParts(parts: {
  color: TeammateColor;
  shape: TeammateAvatarShape | null;
  eyes: TeammateEyesEnum | null;
}): TeammateAvatarConfig {
  const preset = TEAMMATE_AVATAR_PRESETS[parts.color];
  return {
    baseColor: preset.baseColor,
    darkColor: preset.darkColor,
    shape: parts.shape ?? AVATAR_FALLBACK.shape,
    eyes: EYES_BY_ENUM[parts.eyes ?? AVATAR_FALLBACK.eyes],
  };
}

/* ------------------------------------------------------------------ *
 * SB-3519 — the visual seam (#1255)
 *
 * A teammate wears exactly one of two visuals. Everything below decides
 * WHICH, so that no page, card, row or dropdown writes that branch itself.
 * ------------------------------------------------------------------ */

/**
 * Which visual a teammate wears, in the API's own vocabulary
 * (`TeammateView.VisualTypeEnum`). Note `avatar`, not `default`: KD-13
 * renamed it on 2026-08-26 so the word `default` stops carrying two meanings.
 */
export type TeammateVisualType = 'avatar' | 'icon';

/**
 * The icon arm. `darkColor` is the glyph color on the two teammate-colored
 * presentations (the spec's "dark instance of that color"); `baseColor` tints
 * their container. `icon` is the OUTLINE subset, not the whole sprite set
 * (KD-9) — that is what makes `calendar-filled` unstorable here rather than
 * merely unwise.
 */
export interface TeammateIconConfig {
  baseColor: string;
  darkColor: string;
  icon: TeammateIconOutlineName;
}

/** What every render site receives from now on. */
export type TeammateVisual =
  | ({ type: 'avatar' } & TeammateAvatarConfig)
  | ({ type: 'icon' } & TeammateIconConfig);

/**
 * A named surface treatment, resolved once in the seam.
 *
 * Deliberately NOT the avatar's `variant`, which is a size-and-crop axis:
 * three surfaces share `small` while needing different containers, and
 * `card` is not a size at all but the uncropped full-body form. Resolving
 * the treatment by surface name keeps that branch in one place instead of
 * letting each consumer add its own discriminator.
 *
 * `card-header` and `builder-banner` are the clearest case for that. Same
 * avatar variant, same full-body character, same transparent background — and
 * opposite glyph placement, ruled surface by surface (@maxeber, 2026-09-01:
 * « En coin, c'est pour la teammate landing-page et centré, c'est pour le
 * teammate builder »). A `variant` axis could not tell them apart at all.
 */
export type TeammatePresentation =
  | 'card-header'
  | 'builder-banner'
  | 'page-header'
  | 'nav-slot'
  | 'admin-row'
  | 'admin-dropdown';

/**
 * The six named surfaces reduced onto the avatar's four size variants.
 *
 * This lives here rather than in the wrapper because it is now read twice:
 * the wrapper picks the avatar's `variant` from it, and
 * `dropsBaseWhenHighlighted` derives the icon arm's highlight rule from it.
 * Two presentations sharing a variant can still differ in container — that is
 * the whole point of the `presentation` axis — but they can never differ in
 * SIZE, and writing the mapping twice is how the two arms would drift apart.
 *
 * A `Record` over the closed union, so a sixth presentation fails the build
 * instead of silently inheriting a fifth surface's size.
 */
export const PRESENTATION_TO_AVATAR_VARIANT: Record<
  TeammatePresentation,
  'card' | TeammateAvatarCropContext
> = {
  'card-header': 'card',
  // Deliberately the same variant as `card-header`: the Builder banner wears
  // the same full-body character on the avatar arm, and the two presentations
  // differ only in where the ICON arm puts its glyph. Mapping it here is what
  // keeps `TeammateAvatarComponent` byte-identical on that surface — and what
  // gives `dropsBaseWhenHighlighted` the right answer (false) for free.
  'builder-banner': 'card',
  'page-header': 'large',
  'nav-slot': 'small',
  'admin-row': 'small',
  'admin-dropdown': 'x-small',
};

/**
 * Does this presentation drop its base color layer while highlighted?
 *
 * The rule belongs to the DEFAULT visual — `teammate-avatar.component.ts`
 * paints the base under white at 84 % and drops the base layer when `active`
 * or `hovered`, but only on the cropped-and-small branch; `large` always
 * paints it and `card` has its own branch. The spec's Story 1 asks the icon to
 * follow "the same color treatment as the default teammate visual system", so
 * the icon arm reads that rule from here rather than restating it.
 *
 * ⚠️ Vocabulary trap, and it is the reason this is not called `isCropped`:
 * the avatar's own `isCropped()` is `variant !== 'card'`, so `large` IS
 * cropped there while it does NOT drop. This predicate is the narrower
 * question, and answering it by variant — not by presentation — is what keeps
 * it honest if a presentation is ever remapped.
 */
export const dropsBaseWhenHighlighted = (
  presentation: TeammatePresentation,
): boolean => {
  const variant = PRESENTATION_TO_AVATAR_VARIANT[presentation];
  return variant === 'small' || variant === 'x-small';
};

/**
 * SB-3519 Scenario A2, and the load-bearing half of the guard against it.
 *
 * KD-11 (2026-08-18) dropped the API's icon allowlist, so ANY string reaches
 * here — from a request, a seed, or a SQL data fix. An SVG `<use>` pointing at
 * a fragment that does not exist renders NOTHING: no console error, no network
 * error, no Sentry event, just an invisible teammate on every surface. The
 * membership test below is what stops that.
 *
 * Falls back to the library's own `default` glyph (ruled by @maxeber
 * 2026-08-28 on #1255). The dev-mode warning is the only trace a fired
 * fallback leaves: `default` is itself selectable in the picker, so on screen
 * — and in the stored row — it is indistinguishable from a deliberate pick.
 *
 * NOTE: there is no image branch here, and that is deliberate. The skill-logo
 * path does have one (`assistants.icon.helper.ts:65-81` returns `'image'` for
 * any unrecognized string, which its component renders through a bound src
 * attribute) — that shape would turn a stored teammate field into an outbound
 * request from every viewer's browser. Keep this file free of the literal
 * markup tag too: the ticket's verification greps this directory for it.
 */
const OUTLINE_NAMES: ReadonlySet<string> = new Set(TeammateIconOutlineList);

export const ICON_FALLBACK: TeammateIconOutlineName = 'default';

export function toTeammateIconOutlineName(
  value: string | null | undefined,
): TeammateIconOutlineName {
  if (value && OUTLINE_NAMES.has(value)) {
    return value as TeammateIconOutlineName;
  }
  if (isDevMode()) {
    console.warn(
      `[SB-3519] unknown teammate icon ${JSON.stringify(value)} — rendering "${ICON_FALLBACK}"`,
    );
  }
  return ICON_FALLBACK;
}

/**
 * Resolve which visual a teammate wears, and its colors.
 *
 * This is the seam. `getTeammateAvatarFromParts` above still owns the avatar
 * arm — it has seven other callers and its own reference-identity spec — so
 * this function delegates rather than duplicating it.
 */
export function getTeammateVisualFromParts(parts: {
  color: TeammateColor;
  shape: TeammateAvatarShape | null | undefined;
  eyes: TeammateEyesEnum | null | undefined;
  visualType: TeammateVisualType | null | undefined;
  icon: string | null | undefined;
}): TeammateVisual {
  if (parts.visualType === 'icon') {
    // `?? PRESETS.A` because the index is not as safe as its type suggests.
    // `SdCountCellItem.leadingAvatar.color` is declared `string`, not the A–E
    // union, so the cast on the way in is unchecked — and an empty string is
    // not nullish, so it survives a `??` default and lands here as a miss.
    // The sibling default on this path (`icon ?? ''`) is safe precisely because
    // an empty string FAILS the membership test below; an empty color has no
    // such guard, and its failure is louder than the one the seam exists to
    // prevent — a TypeError on `preset.baseColor`, not a missing glyph.
    const preset =
      TEAMMATE_AVATAR_PRESETS[parts.color] ?? TEAMMATE_AVATAR_PRESETS.A;
    return {
      type: 'icon',
      baseColor: preset.baseColor,
      darkColor: preset.darkColor,
      icon: toTeammateIconOutlineName(parts.icon),
    };
  }

  // `?? null` is the delegation boundary. This signature admits `undefined`
  // because `IdAndDisplayNameView` declares all four fields optional, while
  // `getTeammateAvatarFromParts` admits `null` only — and under
  // `exactOptionalPropertyTypes` those are not assignable. The coercion lives
  // here so the callee keeps its zero-touch promise.
  return {
    type: 'avatar',
    ...getTeammateAvatarFromParts({
      color: parts.color,
      shape: parts.shape ?? null,
      eyes: parts.eyes ?? null,
    }),
  };
}

/**
 * The one sanctioned way into `TeammateVisual` from a count-cell row.
 *
 * `SdCountCellItem.leadingAvatar` names its fields differently on purpose
 * (`iconName` rather than `icon`, `visualType` rather than `type`), so the
 * admin teammate dropdowns cannot call `getTeammateVisualFromParts` directly. Without
 * this adapter the shortest path through that mismatch is to hand-build the
 * union — which skips the membership test above, and because a stored
 * `-filled` name IS a real symbol in the same sprite, would draw a filled
 * glyph on a teammate surface and break KD-9.
 */
export function getTeammateVisualFromLeadingAvatar(
  leadingAvatar: SdCountCellItem['leadingAvatar'],
): TeammateVisual {
  // An EMPTY color must fall back like a missing one. `??` would not do it —
  // this field is typed `string` upstream, not the A–E union, so an empty
  // string is representable and is not nullish. Written as an explicit
  // emptiness test rather than `||` so the intent survives a reader, and the
  // repo's prefer-nullish-coalescing rule, without an escape comment.
  const storedColor = leadingAvatar?.color;
  const color = (
    storedColor && storedColor.length > 0 ? storedColor : 'A'
  ) as TeammateColor;
  const isIcon = leadingAvatar?.visualType === 'icon';

  return getTeammateVisualFromParts({
    color,
    visualType: isIcon ? 'icon' : 'avatar',
    icon: isIcon ? leadingAvatar.iconName : null,
    shape: isIcon
      ? null
      : ((leadingAvatar?.shape ?? null) as TeammateAvatarShape | null),
    eyes: isIcon
      ? null
      : ((leadingAvatar?.eyes ?? null) as TeammateEyesEnum | null),
  });
}

/**
 * The fields of a teammate dropdown row that {@link toTeammateCountCellItem}
 * reads: a subset of the API's `IdAndDisplayNameView`, typed structurally so
 * this file keeps importing nothing from the generated client.
 */
export interface TeammateDropdownItemParts {
  displayName: string;
  title?: string | undefined;
  color?: string | undefined;
  visualType?: string | undefined;
  icon?: string | undefined;
  shape?: string | undefined;
  eyes?: string | undefined;
}

/**
 * A teammate dropdown row for `sd-count-cell`: its own visual as the leading
 * tile, its localized title as muted meta. Each calling page renders the tile
 * through its `teammateAvatarTpl`, which resolves `leadingAvatar` with
 * {@link getTeammateVisualFromLeadingAvatar} above. Figma 23014:22003.
 */
export function toTeammateCountCellItem(
  item: TeammateDropdownItemParts,
): SdCountCellItem {
  const cell: SdCountCellItem = { label: item.displayName };
  // `color` belongs to both visuals, so it is forwarded on its own — a row
  // whose visual has no shape or face still draws in the teammate's own
  // color instead of the palette's first entry. The icon arm carries the bare
  // glyph name for the visual seam to render.
  if (item.color) {
    // The discriminator ALONE decides the arm. Guarding on `item.icon` too
    // looked safer and was the opposite. An icon row whose glyph name is
    // empty failed that test, then failed `item.shape && item.eyes` as well
    // — the API nulls shape and eyes on an icon row, and the
    // `Teammate_visual_fields_match_type` CHECK enforces it — and landed on
    // the bare `else`, which carries the color alone.
    // `getTeammateVisualFromLeadingAvatar` then rebuilt a default character
    // from that color through `getTeammateAvatarFromParts`: the bouncing
    // character that the icon visual exists to let a client escape. Nothing
    // ever stored `visualType: 'avatar'`; the wrong visual was assembled by a
    // fallback at the end of the chain. An empty name is writable: the CHECK
    // tests NULL-ness only, and a seed or SQL data fix bypasses the API's
    // non-blank rule. An empty glyph name now stays on the icon arm and
    // resolves to `ICON_FALLBACK` in `toTeammateIconOutlineName`.
    if (item.visualType === 'icon') {
      cell.leadingAvatar = {
        color: item.color,
        visualType: 'icon',
        iconName: item.icon ?? '',
      };
    } else if (item.shape && item.eyes) {
      cell.leadingAvatar = {
        color: item.color,
        visualType: 'avatar',
        shape: item.shape,
        eyes: item.eyes,
      };
    } else {
      cell.leadingAvatar = { color: item.color };
    }
  }
  // Localized role/title as the muted trailing meta.
  if (item.title) {
    cell.meta = item.title;
  }
  return cell;
}
